import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { campaignJobBoardQueryKeys } from "./queryKeys";

import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

interface InvoiceMonthSpentParams {
  campaignId: string;
  clientId: string;
  clientIntegrationModuleId: string;
  year: string;
  month: string;
  amount: number;
}

export function useInvoiceCampaignJobBoardMonthSpentMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError<ValidationErrorResponse>,
    InvoiceMonthSpentParams,
    { previousData?: CampaignJobBoardType }
  >({
    mutationFn: ({ campaignId, clientIntegrationModuleId, clientId, year, month, amount }) => {
      return instance.post(
        `/api/v2/Campaigns/${campaignId}/Integrations/${clientIntegrationModuleId}/Spent/Month/${year}/${month}`,
        { clientId2: clientId, amount },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    },
    onMutate: async ({
      campaignId,
      clientIntegrationModuleId,
      clientId,
      year,
      month,
      amount,
    }) => {
      const queryKey = campaignJobBoardQueryKeys.detail(
        campaignId,
        clientIntegrationModuleId,
        clientId
      );

      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<CampaignJobBoardType>(queryKey);

      if (previousData?.monthlyRecords) {
        const monthString = `${year}-${month.padStart(2, "0")}`;
        const updatedRecords = previousData.monthlyRecords.map((record) => {
          if (
            record.month === monthString ||
            record.month.startsWith(monthString)
          ) {
            const updated: typeof record = {
              ...record,
              actualSpent: amount,
            };

            if (
              record.includeMargin &&
              record.actualSpent != null &&
              record.actualSpent !== 0 &&
              record.spent != null
            ) {
              const marginMultiplier = record.spent / record.actualSpent;
              updated.spent = amount * marginMultiplier;
            } else if (record.includeMargin) {
              updated.spent = amount * 1.21;
            } else {
              updated.spent = amount;
            }

            return updated;
          }
          return record;
        });

        queryClient.setQueryData<CampaignJobBoardType>(queryKey, {
          ...previousData,
          monthlyRecords: updatedRecords,
        });
      }

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        const queryKey = campaignJobBoardQueryKeys.detail(
          variables.campaignId,
          variables.clientIntegrationModuleId,
          variables.clientId
        );
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: async (_, __, { campaignId, clientIntegrationModuleId, clientId }) => {
      const detailKey = campaignJobBoardQueryKeys.detail(
        campaignId,
        clientIntegrationModuleId,
        clientId
      );
      
      // Consolidate all invalidations in onSettled with refetchType: 'all' to force refetch
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.details(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: detailKey,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.lists(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.list(campaignId, clientId),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: campaignJobBoardQueryKeys.months.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: invoiceQueryKeys.cache.transactions.byClient(clientId),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
          refetchType: "all",
        }),
      ]);
      
      // Explicitly refetch the detail query to ensure immediate update
      await queryClient.refetchQueries({
        queryKey: detailKey,
        type: "active",
      });
    },
  });
}
